import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InternalNavbar from "../../components/feature/InternalNavbar";

interface UseCase {
  icon: string;
  titleEs: string;
  titleEn: string;
  painEs: string;
  painEn: string;
  solutionEs: string;
  solutionEn: string;
  metricEs: string;
  metricEn: string;
  color: string;
}

const useCases: UseCase[] = [
  {
    icon: "ri-hospital-line",
    titleEs: "Clínicas dentales", titleEn: "Dental Clinics",
    painEs: "Las citas canceladas de último minuto y las llamadas no respondidas durante el tratamiento cuestan dinero real cada día.",
    painEn: "Last-minute cancellations and unanswered calls during treatment cost real money every single day.",
    solutionEs: "Auria confirma citas 24h antes, rellena huecos de agenda automáticamente y gestiona reprogramaciones sin intervención humana.",
    solutionEn: "Auria confirms appointments 24h ahead, auto-fills cancelled slots and handles rescheduling without human intervention.",
    metricEs: "−40% llamadas perdidas · +25% ocupación", metricEn: "−40% missed calls · +25% occupancy",
    color: "text-rose-500",
  },
  {
    icon: "ri-heart-pulse-line",
    titleEs: "Centros médicos", titleEn: "Medical Centres",
    painEs: "Urgencias y consultas fuera de horario que se desvían a la competencia por falta de atención inmediata.",
    painEn: "After-hours emergencies and queries that go to the competition simply because nobody answers.",
    solutionEs: "Triaje inicial 24/7, derivación a guardia si la situación es urgente, y agenda de la primera cita disponible en tiempo real.",
    solutionEn: "24/7 initial triage, on-call escalation for urgent cases, and real-time booking of the first available appointment.",
    metricEs: "Disponibilidad 24/7 · 31 idiomas nativos", metricEn: "24/7 availability · 31 native languages",
    color: "text-emerald-500",
  },
  {
    icon: "ri-scales-3-line",
    titleEs: "Asesorías y despachos", titleEn: "Law Firms & Advisories",
    painEs: "Clientes que llaman, no consiguen hablar con nadie y firman con la competencia esa misma tarde.",
    painEn: "Clients who call, get no answer and sign with a competitor the same afternoon.",
    solutionEs: "Recoge datos del caso, agenda la primera consulta y sincroniza con el CRM del despacho sin intervención del equipo.",
    solutionEn: "Captures case details, books the first consultation and syncs with the firm's CRM without staff involvement.",
    metricEs: "+35% leads cualificados · 0 llamadas perdidas", metricEn: "+35% qualified leads · 0 missed calls",
    color: "text-amber-500",
  },
  {
    icon: "ri-restaurant-line",
    titleEs: "Restaurantes y hostelería", titleEn: "Restaurants & Hospitality",
    painEs: "El teléfono suena en pleno servicio y nadie puede cogerlo. Las reservas se van a TripAdvisor o se pierden.",
    painEn: "The phone rings during service rush and nobody can answer. Bookings go elsewhere or are simply lost.",
    solutionEs: "Gestiona reservas, informa del menú del día, recoge alergias y confirma disponibilidad las 24h sin interrumpir al equipo.",
    solutionEn: "Handles bookings, shares the daily menu, records allergies and confirms availability 24/7 without interrupting the team.",
    metricEs: "Reservas 24/7 · Integración Google Calendar", metricEn: "24/7 bookings · Google Calendar sync",
    color: "text-orange-500",
  },
  {
    icon: "ri-building-2-line",
    titleEs: "Inmobiliarias", titleEn: "Real Estate Agencies",
    painEs: "Un lead caliente espera 3 horas para ser atendido y ya firmó con la competencia antes de que alguien le llamara.",
    painEn: "A hot lead waits 3 hours to be contacted and has already signed with a competitor before anyone calls back.",
    solutionEs: "Responde al instante, cualifica el lead con preguntas clave y agenda visitas automáticamente en el calendario del agente.",
    solutionEn: "Responds instantly, qualifies the lead with key questions and auto-schedules visits directly in the agent's calendar.",
    metricEs: "−60% tiempo de respuesta · Cualificación IA", metricEn: "−60% response time · AI qualification",
    color: "text-sky-500",
  },
  {
    icon: "ri-graduation-cap-line",
    titleEs: "Academias y formación", titleEn: "Training Centres & Academies",
    painEs: "Decenas de consultas sobre matrículas y cursos llegan fuera del horario de administración y nunca se responden.",
    painEn: "Dozens of enquiries about enrolment and courses arrive outside office hours and never get answered.",
    solutionEs: "Informa sobre cursos, precios, fechas y proceso de inscripción en 31 idiomas sin que nadie esté en la oficina.",
    solutionEn: "Provides details on courses, fees, dates and enrolment in 31 languages with nobody in the office.",
    metricEs: "31 idiomas · Consultas 24/7", metricEn: "31 languages · 24/7 enquiries",
    color: "text-violet-500",
  },
];

const stats = [
  { value: "6+", labelEs: "sectores activos", labelEn: "active sectors" },
  { value: "+2.400", labelEs: "negocios confían en Auria", labelEn: "businesses trust Auria" },
  { value: "31", labelEs: "idiomas nativos", labelEn: "native languages" },
  { value: "99.7%", labelEs: "uptime garantizado", labelEn: "guaranteed uptime" },
];

export default function CasosDeUsoPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";

  const navLinks = [
    { label: "Blog", href: "/blog" },
    { label: "Roadmap", href: "/roadmap" },
    { label: isEn ? "About us" : "Sobre nosotros", href: "/sobre-nosotros" },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <InternalNavbar links={navLinks} rightCta={{ label: isEn ? "See plans" : "Ver planes", href: "/" }} />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden py-24 px-6" style={{ background: "#0C0C0E" }}>
          <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(ellipse 80% 60% at 50% -10%, #f59e0b 0%, transparent 70%)" }} />
          <div className="relative max-w-3xl mx-auto text-center">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-400 mb-5">
              {isEn ? "Use cases" : "Casos de uso"}
            </span>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isEn
                ? "Every business that can't miss a call"
                : "Cada negocio que no puede perder una llamada"}
            </h1>
            <p className="text-stone-400 text-lg leading-relaxed max-w-2xl mx-auto">
              {isEn
                ? "Auria adapts to every sector. From dental clinics to law firms — if the phone is your lifeline, we've got you covered."
                : "Auria se adapta a cualquier sector. Desde clínicas dentales hasta despachos jurídicos — si el teléfono es tu canal principal, nosotros lo cubrimos."}
            </p>
          </div>
        </section>

        {/* Use cases grid */}
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
                {isEn ? "By sector" : "Por sector"}
              </span>
              <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isEn ? "See how Auria works in your industry" : "Así funciona Auria en tu sector"}
              </h2>
              <p className="text-stone-500 text-sm mt-3 max-w-xl mx-auto">
                {isEn
                  ? "Same technology, customised for the real workflows of each vertical."
                  : "La misma tecnología, personalizada para los flujos reales de cada vertical."}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((uc) => (
                <div key={uc.titleEs} className="bg-white border border-stone-100 rounded-2xl p-7 flex flex-col gap-4 hover:border-stone-200 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-50">
                      <i className={`${uc.icon} ${uc.color} text-xl`} />
                    </div>
                    <h3 className="font-bold text-stone-900 text-base">{isEn ? uc.titleEn : uc.titleEs}</h3>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-stone-400 mb-1">
                      {isEn ? "The problem" : "El problema"}
                    </p>
                    <p className="text-stone-600 text-sm leading-relaxed">{isEn ? uc.painEn : uc.painEs}</p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-1">
                      {isEn ? "What Auria does" : "Qué hace Auria"}
                    </p>
                    <p className="text-stone-600 text-sm leading-relaxed">{isEn ? uc.solutionEn : uc.solutionEs}</p>
                  </div>

                  <div className="mt-auto pt-3 border-t border-stone-50">
                    <p className={`text-xs font-bold ${uc.color}`}>{isEn ? uc.metricEn : uc.metricEs}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mid CTA band */}
        <section className="py-16 px-6" style={{ background: "#FAFAF9" }}>
          <div className="max-w-4xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-xl">
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-3">
                {isEn ? "Your sector too" : "Tu sector también"}
              </span>
              <h2 className="text-2xl font-bold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isEn
                  ? "Don't see your industry? We configure Auria for any business model."
                  : "¿No ves tu sector? Configuramos Auria para cualquier modelo de negocio."}
              </h2>
              <p className="text-stone-500 text-sm leading-relaxed">
                {isEn
                  ? "Spas, physiotherapy clinics, veterinary centres, insurance agencies, car dealers — if your phone is a lead source, Auria can handle it."
                  : "Spas, centros de fisioterapia, clínicas veterinarias, agencias de seguros, concesionarios — si tu teléfono es una fuente de leads, Auria puede gestionarlo."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link to="/" className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm">
                {isEn ? "See plans" : "Ver planes"}
              </Link>
              <a href="mailto:auriareceptionistai@gmail.com" className="px-6 py-3 bg-white border border-stone-200 hover:border-stone-300 text-stone-800 font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm">
                {isEn ? "Talk to the team" : "Hablar con el equipo"}
              </a>
            </div>
          </div>
        </section>

        {/* Dark CTA */}
        <section className="py-20 px-6" style={{ background: "#0C0C0E" }}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isEn ? "Ready to stop missing calls?" : "¿Listo para no perder más llamadas?"}
            </h2>
            <p className="text-stone-400 text-sm mb-8">
              {isEn
                ? "Set up in 15 minutes. No credit card. Cancel whenever you want."
                : "Configuración en 15 minutos. Sin tarjeta de crédito. Cancela cuando quieras."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm">
                {isEn ? "See plans and pricing" : "Ver planes y precios"}
              </Link>
              <a href="mailto:auriareceptionistai@gmail.com" className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm border border-white/10">
                {isEn ? "Contact the team" : "Contactar con el equipo"}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-stone-50 border-t border-stone-100 px-6 py-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-400 text-xs">{isEn ? "© 2026 Auria. All rights reserved." : "© 2026 Auria. Todos los derechos reservados."}</p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "Home" : "Inicio"}</Link>
            <Link to="/blog" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">Blog</Link>
            <Link to="/sobre-nosotros" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "About" : "Sobre nosotros"}</Link>
            <Link to="/privacidad" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">{isEn ? "Privacy" : "Privacidad"}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
